import React, { useRef, useState } from "react";
import Button from "../../form/Button";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../elements/front/Modal";
import {
  profileCoverImageUpdate,
  profileImageUpdate,
  profileUpdate,
} from "../../../services/ProfileService";
import { useNavigate } from "react-router-dom";
import { CreateChat } from "../../../services/communicationService";
import ManageProfile from "./ManageProfile";
import defultprofileCover from "../../../assets/image/defultprofileCover.jpg";
import defultprofileAvatar from "../../../assets/image/profile-avatar.webp";
import { Camera, Edit2, PencilLine } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import StartChatModal from "./StartChatModal";
import { setProfile } from "../../../redux/slice/profileSlice";

const ProfileTotalDetails = ({ user }) => {
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isStartChatOpen, setIsStartChatOpen] = React.useState(false);
  const [isStartingChat, setIsStartingChat] = React.useState(false);
  const [formData, setFormData] = React.useState(user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.authProvider);
  const isOwnProfile = session?.user?._id === user?._id;
  const profileImageRef = useRef(null);
  const profileCoverRef = useRef(null);
  const queryClient = useQueryClient();
  const handleSave = async () => {
    try {
      await profileUpdate(formData); // your API
      setIsEditOpen(false);
    } catch (err) {
      console.log(err);
    }
  };
  const { mutate: updateProfileImage, isPending: isUploadingProfile } =
    useMutation({
      mutationFn: async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        return await profileImageUpdate(formData);
      },
      onSuccess: (res) => {
        queryClient.invalidateQueries(["profile-details"]);
        if (res?.data) {
          dispatch(setProfile(res.data)); // 🔥 instant UI update everywhere
        }
      },
    });
  const { mutate: updateCoverImage, isPending: isUploadingCover } = useMutation(
    {
      mutationFn: async (file) => {
        const formData = new FormData();
        formData.append("coverMedia", file);
        return await profileCoverImageUpdate(formData);
      },
      onSuccess: (res) => {
        queryClient.invalidateQueries(["profile-details"]);
        if (res?.data) {
          dispatch(setProfile(res.data)); // 🔥 instant UI update everywhere
        }
      },
    },
  );
  const handleStartChat = async (content) => {
    const currentUserId = session?.user?._id;
    const receiverId = user?._id;

    if (!currentUserId || !receiverId) return;

    const trimmed = String(content || "").trim();
    if (!trimmed) {
      toast.warning("Please type a message.");
      return;
    }

    const payload = {
      recipientType: "user",
      // organization: user?.organization?._id,
      inboxType: "personal",
      type: "chat",
      createdBy: currentUserId,
      customerId: receiverId,

      message: {
        // user: currentUserId,
        name: session?.user?.firstName || "User",
        content: trimmed,
        /*
        content: "Hi 👋",
        */
      },
    };

    setIsStartingChat(true);
    try {
      const res = await CreateChat(payload);

      const chatId = res?.data?.communicationGroupId;
      if (chatId) {
        setIsStartChatOpen(false);
        navigate(`/messages?chatId=${chatId}`);
      }
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to start chat.",
      );
    } finally {
      setIsStartingChat(false);
    }
  };
  const {
    firstName = "",
    lastName = "",
    email = "",
    image = null,
    organization = {},
    coverMedia = {},
    bio = "",
  } = user || {};

  const fullName = `${firstName || ""} ${lastName || ""}`;

  // ✅ Profile Image
  const profileImage = image?.url || defultprofileAvatar;

  // ✅ Profile Cover Image (NOT org)
  const profileCover = coverMedia?.url || defultprofileCover;

  // ✅ Organization (safe destructure)
  const orgName = organization?.content?.en?.title;
  const orgLogo = organization?.logo?.url?.url;
  const orgAddress = organization?.address?.[0];
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Cover Image */}
      <div className="relative">
        <div className="relative">
          <img
            src={profileCover}
            alt="cover"
            className={`w-full h-24 md:h-40 object-cover transition duration-300 ${
              isUploadingCover ? "opacity-50 blur-sm" : ""
            }`}
          />

          {/* 🔥 LOADER OVERLAY */}
          {isUploadingCover && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
        {isOwnProfile && (
          <>
            <button
              onClick={() => profileCoverRef.current.click()}
              className="p-1 flex items-center justify-center bg-zinc-600 hover:bg-palatte hover:scale-105 cursor-pointer rounded-full text-white absolute top-2 right-3"
            >
              <Camera size={18} />
            </button>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              ref={profileCoverRef}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) updateCoverImage(file);
              }}
            />
          </>
        )}

        {/* Profile Image */}
        <div className="absolute -bottom-10 md:-bottom-12 left-4 md:left-6">
          <div className="relative">
            <img
              src={profileImage}
              alt={fullName}
              className={`w-20 h-20 md:w-28 md:h-28 rounded-full border-4 border-white object-cover ${
                isUploadingProfile ? "opacity-50" : ""
              }`}
            />

            {/* 🔥 LOADER */}
            {isUploadingProfile && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          {isOwnProfile && (
            <>
              <button
                onClick={() => profileImageRef.current.click()}
                className="p-1 flex items-center justify-center bg-zinc-600 hover:bg-palatte hover:scale-105 cursor-pointer rounded-full text-white absolute bottom-4 md:bottom-8 -right-2"
              >
                <Camera size={18} />
              </button>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                ref={profileImageRef}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) updateProfileImage(file);
                }}
              />
            </>
          )}
        </div>
        {isOwnProfile && (
          <Button
            buttonIcon={"fa-solid fa-pencil"}
            buttonFunction={() => setIsEditOpen(true)}
            buttonClasses="absolute right-6 -bottom-16 text-gray-500 h-8 w-8 bg-gray-100 hover:bg-gray-200 hover:scale-105 cursor-pointer rounded-full"
          />
        )}
      </div>

      {/* Info Section */}
      <div className="pt-12 px-6 pb-6">
        <h2 className="text-lg font-semibold text-gray-900">{fullName}</h2>

        <p className="text-sm text-gray-600">{email}</p>
        {user?.bio && <p className="text-sm text-gray-700 mt-2">{user.bio}</p>}
        <p className="text-sm text-gray-500 mt-1">{orgName || ""}</p>

        <p className="text-sm text-gray-500">
          {orgAddress
            ? `${orgAddress.city}, ${orgAddress.state}, ${orgAddress.country}`
            : "No location"}
        </p>
        {/* Buttons */}
        <div className="flex gap-3 mt-4">
          {isOwnProfile ? (
            <>
              {/* <Button
              buttonLabel="Edit Profile"
              buttonFunction={() => setIsEditOpen(true)}
              buttonClasses="bg-blue-600 text-white px-4 h-9 rounded-full"
            /> */}
            </>
          ) : (
            <>
              <Button
                buttonLabel="Connect"
                buttonClasses="bg-blue-600 text-white px-4 h-9 rounded-full"
              />
              <Button
                buttonLabel="Message"
                buttonClasses="border px-4 h-9 rounded-full"
                buttonFunction={() => {
                  setIsStartChatOpen(true);
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Organization Card */}
      <div className="px-6 pb-6 border-t pt-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Company</h3>

        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition"
          onClick={() => {
            if (organization?.slug) {
              navigate(`/company/${organization.slug}`);
            }
          }}
        >
          <img
            src={organization?.logo?.url?.url || defultprofileAvatar}
            alt="org"
            className="w-10 h-10 rounded"
          />
          <div>
            <p className="text-sm font-medium">
              {organization?.content?.en?.title || ""}
            </p>
            <p className="text-xs text-gray-500">
              {organization?.content?.en?.content || ""}
            </p>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Profile"
        size="lg"
      >
        <ManageProfile user={user} close={() => setIsEditOpen(false)} />
      </Modal>

      <StartChatModal
        isOpen={isStartChatOpen}
        onClose={() => setIsStartChatOpen(false)}
        title="Message"
        subtitle={`Send a message to ${firstName || "this user"}`}
        isSending={isStartingChat}
        onSend={handleStartChat}
      />
    </div>
  );
};

export default ProfileTotalDetails;
